import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import {StyledGridCol, StyledGridRow} from "@/common/styles";
import TextField from "@/common/elements/InputElements/TextField";
import {BookingsSectionTitle} from "../styles";
import CommonDatePicker from "@/common/elements/DatePicker";
import {getOptionsFromRefList, isNonEmptyVariable, convertApiFields, getSQLTypeToHTMLType} from "@/util/common_function";
import {Form, Input, InputNumber} from "antd";
import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";
import DynamicForm from "@/common/DynamicForm";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const BookingHoldsForm = forwardRef(({selectedHold, holdFormRef, startingPoint}, ref) => {
    const [holdForm] = Form.useForm();
    const [formData, setFormData] = useState(null);
    const [holdStatusType, setHoldStatusType] = useState(52180003)
    const [filterCharge, setFilterCharge] = useState(52180003)
    const fullSearch = useSelector(state => state.fullSearch);

    // -----------------------------------------------------------------------
    // HoldsExtra — PCI's per-client dynamic "_U" columns for this hold
    // (HoldsInterface.asp's "Agency-Specific Information" section, shown
    // right after Comments).
    // -----------------------------------------------------------------------
    const [holdsExtraFields, setHoldsExtraFields] = useState([]);
    const [holdsExtraFieldsReady, setHoldsExtraFieldsReady] = useState(false);
    const holdsExtraTypeMapRef = useRef({});
    const holdsExtraIdRef = useRef(0);

    useEffect(() => {
        api
            .get(`${API_ENDPOINTS.getTableColumns}/HoldsExtra`)
            .then((response) => {
                const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
                const converted = convertApiFields(tableData, "HoldsExtra");
                const typeMap = {};
                converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
                holdsExtraTypeMapRef.current = typeMap;
                setHoldsExtraFields(converted);
                setHoldsExtraFieldsReady(true);
            })
            .catch((error) => {
                console.error("[BookingHoldsForm] HoldsExtra field defs fetch failed:", error);
            });
    }, []);

    // Fetches the saved HoldsExtra row when editing an existing hold — a
    // brand new hold has no holdID yet, so there's nothing to fetch.
    useEffect(() => {
        if (!holdsExtraFieldsReady) return;
        const id = selectedHold?.holdID;
        if (!isNonEmptyVariable(id)) {
            holdsExtraIdRef.current = 0;
            return;
        }

        fetchExtraTableRecord("HoldsExtra", "holdID", id)
            .then((result) => {
                if (!result) return;
                // Field defs list both HoldExtraID and HoldsExtraID as
                // candidate PK names for this table — read whichever the API
                // actually returned rather than assuming one.
                holdsExtraIdRef.current = result.raw?.HoldsExtraID ?? result.raw?.HoldExtraID ?? 0;
                const withDates = Object.keys(result.values).reduce((acc, key) => {
                    const val = result.values[key];
                    const htmlType = holdsExtraTypeMapRef.current[key];
                    const isDateField = htmlType === "date" || htmlType === "datetime";
                    acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
                    return acc;
                }, {});
                holdForm.setFieldsValue(withDates);
            })
            .catch((error) => {
                console.error("[BookingHoldsForm] HoldsExtra fetch failed:", error);
            });
    }, [selectedHold?.holdID, holdsExtraFieldsReady]);

    useEffect(() => {
        setFormData(() => ({
            ...selectedHold,
            holdReason: selectedHold?.holdReason ? parseInt(selectedHold?.holdReason) : null,
            holdStatus: selectedHold?.holdStatus ? parseInt(selectedHold?.holdStatus) : fullSearch ? null : 52180003,
            chargeDescriptionId: selectedHold?.chargeDescriptionId ? parseInt(selectedHold?.chargeDescriptionId) : null,
            expiresDateTime: isNonEmptyVariable(selectedHold?.expiresDate) ? dayjs(`${selectedHold.expiresDate} ${selectedHold.expiresTime}`) : null,
            clearanceAuthorizationDateTime: isNonEmptyVariable(selectedHold?.ClearanceAuthorizationDate) ? dayjs(`${selectedHold?.ClearanceAuthorizationDate} ${selectedHold?.clearanceAuthorizationTime}`) : null,
            transferAuthorizationDateTime: isNonEmptyVariable(selectedHold?.transferAuthorizationDate) ? dayjs(`${selectedHold?.transferAuthorizationDate} ${selectedHold?.transferAuthorizationTime}`) : null,
            transferDateTime: isNonEmptyVariable(selectedHold?.transferDate) ? dayjs(`${selectedHold?.transferDate} ${selectedHold?.transferTime}`) : null,
            transferReleaseDateTime: isNonEmptyVariable(selectedHold?.transferReleaseDate) ? dayjs(`${selectedHold?.transferReleaseDate} ${selectedHold?.transferReleaseTime}`) : null,
        }))

        setHoldStatusType(parseInt(selectedHold?.holdStatus))
    }, [selectedHold]);

    useEffect(() => {
        holdForm.setFieldsValue(formData);
    }, [formData]);

    useEffect(() => {
        holdStatusType === 52180001 && holdForm.setFieldValue('clearanceAuthorizationDateTime', dayjs(new Date()));
        holdStatusType === 52180001 && holdForm.setFieldValue('expiresDateTime', dayjs(new Date()));
    }, [holdStatusType])

    useImperativeHandle(holdFormRef, () => ({
        form: holdForm,
        holdsExtraFieldKeys: holdsExtraFields.map((f) => f.columnName),
        holdsExtraTypeMap: holdsExtraTypeMapRef.current,
        holdsExtraID: holdsExtraIdRef.current
    }))

    const handleStatusTypeChange = (e) => {
        setHoldStatusType(e)
    }

    const { bookingsFieldsEnabled } = useSelector(
        (state) => state.bookings
    );

    const filterChagreDd = () => {
        if(filterCharge?.target?.value) {
            holdForm.setFieldValue('chargeDescriptionId', null);
            return getOptionsFromRefList("Charges", false, false, true)?.filter(item => item?.label?.toLowerCase()?.includes(filterCharge?.target?.value?.toLowerCase()))
        } else {
            return getOptionsFromRefList("Charges", false, false, true)
        }
    }

    const handleChange = (e) => {
        const newValue = e?.target?.value.replace(/\D/g, "");
        holdForm.setFieldValue('holdContactPhone', newValue);
    };

    return (
        <FieldSecurityProvider
            entity={SCHEMA_NAME.HOLDS}
            startingPoint={startingPoint}
            bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
            <>
                <BookingsSectionTitle style={{marginTop: "0"}}>
                    <span>Hold Information</span>
                </BookingsSectionTitle>

                <Form form={holdForm}>
                    <StyledGridRow gutter={16}>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="holdReason" formItemProps={{ name: "holdReason", label: "Hold Reason" }}>
                                <Select
                                    options={getOptionsFromRefList("Hold_Reason", false, false, true)}
                                    placeholder="Hold Reason"
                                    showSearch={true}
                                    variant="outlined"
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="holdFor" formItemProps={{ name: "holdFor", label: "Hold For" }}>
                                <TextField />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="holdContact" formItemProps={{ name: "holdContact", label: "Hold Contact" }}>
                                <TextField />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="holdContactPhone" formItemProps={{ name: "holdContactPhone", label: "Hold Contact Phone" }}>
                                <Input type="text" onChange={handleChange} />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="holdStatus" formItemProps={{ name: "holdStatus", label: "Hold Status" }}>
                                <Select
                                    options={getOptionsFromRefList("Hold_Status", false, false, true)}
                                    placeholder="Hold Status"
                                    showSearch={true}
                                    variant="outlined"
                                    onChange={(e) => handleStatusTypeChange(e)}
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="expiresDateTime" formItemProps={{ name: "expiresDateTime", label: "Expires Date & Time" }}>
                                <CommonDatePicker showTime placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" />
                            </SecureFormItem>
                        </StyledGridCol>

                    </StyledGridRow>

                    <BookingsSectionTitle>
                        <span>Charge Information</span>
                    </BookingsSectionTitle>

                    <StyledGridRow gutter={16}>

                        {/* Filter (no field security) */}
                        <StyledGridCol span={6}>
                            <FormItem label="Filter">
                                <TextField onChange={(e) => setFilterCharge(e)} />
                            </FormItem>
                        </StyledGridCol>

                        <StyledGridCol span={12}>
                            <SecureFormItem fieldName="chargeDescriptionId" formItemProps={{ name: "chargeDescriptionId", label: "Charge" }}>
                                <Select
                                    options={filterChagreDd()}
                                    placeholder="Charge"
                                    showSearch
                                    variant="outlined"
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol span={6}>
                            <SecureFormItem fieldName="chargeLevelID" formItemProps={{ name: "chargeLevelID", label: "Level" }}>
                                <Select
                                    options={getOptionsFromRefList("Charge_Level", false, false, true)}
                                    placeholder="Level"
                                    showSearch
                                    variant="outlined"
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                    </StyledGridRow>

                    {holdStatusType === 52180001 && (
                        <>
                            <BookingsSectionTitle>
                                <span>Clearance Information</span>
                            </BookingsSectionTitle>

                            <StyledGridRow gutter={16}>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="clearanceOfficerStaffId" formItemProps={{ name: "clearanceOfficerStaffId", label: "Clearance Officer" }}>
                                        <Select options={getOptionsFromRefList("", true)} placeholder="Clearance Officer" showSearch />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="clearanceReasonID" formItemProps={{ name: "clearanceReasonID", label: "Reason" }}>
                                        <Select options={getOptionsFromRefList("Clearance_Reason", false, false, true)} placeholder="Reason" showSearch />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="clearanceAuthorizationDateTime" formItemProps={{ name: "clearanceAuthorizationDateTime", label: "Entered Date & Time" }}>
                                        <CommonDatePicker showTime placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY hh:mm" />
                                    </SecureFormItem>
                                </StyledGridCol>

                            </StyledGridRow>
                        </>
                    )}

                    {holdStatusType === 52180002 && (
                        <>
                            <BookingsSectionTitle>
                                <span>Transfer Information</span>
                            </BookingsSectionTitle>

                            <StyledGridRow gutter={16}>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="transferAuthorizedBy" formItemProps={{ name: "transferAuthorizedBy", label: "Authorizing Officer" }}>
                                        <TextField />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="transferEnteredBy" formItemProps={{ name: "transferEnteredBy", label: "Entered By" }}>
                                        <TextField />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="transferAuthorizationDateTime" formItemProps={{ name: "transferAuthorizationDateTime", label: "Entered Date & Time" }}>
                                        <CommonDatePicker showTime placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY hh:mm" />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="transferDateTime" formItemProps={{ name: "transferDateTime", label: "Scheduled Date & Time" }}>
                                        <CommonDatePicker showTime placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY hh:mm" />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="releasedTo" formItemProps={{ name: "releasedTo", label: "Released To" }}>
                                        <TextField />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="releasedBy" formItemProps={{ name: "releasedBy", label: "Released By" }}>
                                        <TextField />
                                    </SecureFormItem>
                                </StyledGridCol>

                                <StyledGridCol span={6}>
                                    <SecureFormItem fieldName="transferReleaseDateTime" formItemProps={{ name: "transferReleaseDateTime", label: "Release Date & Time" }}>
                                        <CommonDatePicker showTime placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY hh:mm" />
                                    </SecureFormItem>
                                </StyledGridCol>

                            </StyledGridRow>
                        </>
                    )}

                    <BookingsSectionTitle>
                        <span>Comments</span>
                    </BookingsSectionTitle>

                    <SecureFormItem fieldName="comments" formItemProps={{ name: "comments" }}>
                        <TextField inputType="textarea" />
                    </SecureFormItem>

                    {holdsExtraFields.length > 0 && (
                        <>
                            <BookingsSectionTitle>
                                <span>Agency-Specific Information</span>
                            </BookingsSectionTitle>
                            <StyledGridRow gutter={16}>
                                {holdsExtraFields.map((sf, index) => (
                                    <DynamicForm
                                        key={index}
                                        formAttributes={sf}
                                        form={holdForm}
                                        bookingsFieldsEnabled={bookingsFieldsEnabled}
                                    />
                                ))}
                            </StyledGridRow>
                        </>
                    )}

                </Form>
            </>
        </FieldSecurityProvider>
    );
});

export default BookingHoldsForm;